import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Container,
  Grid,
  IconButton,
  makeStyles,
  Slide,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { ApiFunctions } from 'hydra-data-types';
import { FunctionComponent, useEffect, useState } from 'react';
import { match, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import connectMindsetLogo from '../../assets/svgs/logos/connect-mindset-logo.svg';
import nervaLogoWithName from '../../assets/svgs/logos/nerva-logo-with-name.svg';
import { CONNECT_SCREENS, REFERRAL_URL_TYPE } from '../../constants';
import { Analytics } from '../../helpers/analytics/Analytics';
import { AnalyticsEventName } from '../../helpers/analytics/AnalyticsEvent';
import { QRCODE_QUERY_PARAM } from '../../helpers/ReferralUtils';
import { isNonEmptyString } from '../../helpers/StringUtils';
import { useAuthDispatch, useAuthStore } from '../../store/AuthStore';
import { ActionType } from '../../store/StoreTypes';
import { colors } from '../../theme/colors';
import { LoginScreen } from './LoginScreen';
import { PasswordScreen } from './PasswordScreen';
import { HCP_ACCESSING_REFERRAL_LINK } from './PatientConsentScreen';
import { RegisterScreen } from './RegisterScreen';
import { ResetPasswordScreen } from './ResetPasswordScreen';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    paddingBottom: '20px',
    paddingTop: `${theme.spacing(3)}px`,
    [theme.breakpoints.up('sm')]: {
      paddingTop: `${theme.spacing(8)}px`,
    },
  },
  cardGrid: { width: '100%' },
  card: { height: '100%', borderRadius: 30, padding: theme.spacing(3) },
  cardHeaderTextCenter: { textAlign: 'center' },
  cardAction: { '& .MuiCardHeader-action': { flex: '0 0 auto' } },
  cardHeaderTextLeft: { textAlign: 'left' },
  cardHeaderText: { color: colors.green },
}));

export const SignInOrRegisterScreen: FunctionComponent = () => {
  const userIdMatch: match<{ uid: string }> | null = useRouteMatch('/patient/:uid');
  const { search } = useLocation();
  const history = useHistory();
  const hcpReferralHash = useAuthStore((state) => state.hcpReferralHash);
  const hasHcpReferralHash = isNonEmptyString(hcpReferralHash);
  const dispatch = useAuthDispatch();
  const classes = useStyles({ hcpReferralHash });
  const [doesUserExist, setDoesUserExist] = useState<ApiFunctions.DoesUserExistResult>();
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [step, setStep] = useState(0);

  const isFromQrCode = search === QRCODE_QUERY_PARAM;
  const referralUrlType = isFromQrCode ? REFERRAL_URL_TYPE.QRCODE : REFERRAL_URL_TYPE.LINK;

  useEffect(function trackAnalytics() {
    Analytics.track({
      event: AnalyticsEventName.h_screen_viewed,
      screen: CONNECT_SCREENS.SIGN_IN_OR_REGISTER,
    });
  }, []);

  useEffect(
    function setReferralUrlTypeAndReferralHash() {
      dispatch({ type: ActionType.SET_REFERRAL_URL_TYPE, referralUrlType });

      if (isNonEmptyString(userIdMatch?.params.uid)) {
        if (userIdMatch?.params.uid?.length === 10) {
          dispatch({
            type: ActionType.SET_HCP_PATIENT_LINK_ID,
            hcpReferralHash: userIdMatch?.params.uid,
          });
          sessionStorage.setItem('hcpReferralHash', userIdMatch?.params.uid);
          Analytics.track({
            event: AnalyticsEventName.h_referral_url_opened,
            type: referralUrlType,
          });
        } else {
          history.push('/users/patient/invalid-link');
        }
      }
    },
    [dispatch, userIdMatch?.params.uid, history]
  );

  function choosetitle(stepNumber: number, hasMindsetId: boolean | undefined) {
    if (stepNumber === 0) {
      return 'Enter your email';
    }
    if (stepNumber === 1 && hasMindsetId) {
      return 'Enter your password';
    }
    if (stepNumber === 3) {
      return 'Reset your password';
    }
    return 'Create your account';
  }

  const hcpLoggedInWithReferralLink = history.location.state === HCP_ACCESSING_REFERRAL_LINK;
  return (
    <Container maxWidth="sm">
      <Grid container direction="column" alignItems="center" classes={{ root: classes.root }}>
        <Grid container item direction="column" alignItems="center">
          <Box marginBottom="10px">
            {hasHcpReferralHash ? (
              <img src={nervaLogoWithName} alt="Nerva Logo" width={220} height={93} />
            ) : (
              <img src={connectMindsetLogo} alt="Connect logo" width={300} height={100} />
            )}
          </Box>

          <Box maxWidth={470} textAlign="center" marginBottom="70px">
            {hasHcpReferralHash ? (
              <Typography>
                Start your Nerva journey by entering your details and linking with your healthcare
                practitioner
              </Typography>
            ) : (
              <>
                <Typography>
                  Enable healthcare provider access to Mindset Health Programs (Nerva, Evia, Finito,
                  Claria and Relio).
                </Typography>
              </>
            )}
          </Box>
        </Grid>
        <Grid container item classes={{ root: classes.cardGrid }} direction="column">
          <Card classes={{ root: classes.card }} elevation={1}>
            <CardHeader
              title={choosetitle(step, doesUserExist?.hasMindsetId)}
              action={
                step > 0 && (
                  <IconButton
                    aria-label="go back"
                    onClick={() => {
                      setStep(step === 3 ? 1 : step - 1);
                    }}
                    color="primary"
                  >
                    <FontAwesomeIcon icon={['far', 'long-arrow-left']} size="sm" />
                  </IconButton>
                )
              }
              classes={{
                root: `${
                  (step === 1 && !doesUserExist?.hasMindsetId) || step === 2
                    ? classes.cardHeaderTextLeft
                    : `${classes.cardHeaderTextCenter} ${classes.cardAction}`
                } ${classes.cardHeaderText}`,
              }}
            />

            <CardContent>
              {step === 0 && (
                <Slide direction="left" in mountOnEnter unmountOnExit>
                  <div>
                    <LoginScreen
                      setStep={setStep}
                      setDoesUserExist={setDoesUserExist}
                      setEmail={setEmail}
                      hcpLoggedInWithReferralLink={hcpLoggedInWithReferralLink}
                    />
                  </div>
                </Slide>
              )}

              {step === 1 && !doesUserExist?.hasMindsetId && (
                <RegisterScreen email={email} password={password} />
              )}

              {step === 1 && doesUserExist?.hasMindsetId && (
                <Slide direction="left" in mountOnEnter unmountOnExit>
                  <div>
                    <Alert severity="info">
                      Looks like you already have an account with Mindset Health, you can login
                      using the same password.
                    </Alert>
                    <br />
                    <br />
                    <PasswordScreen
                      setStep={setStep}
                      setPassword={setPassword}
                      email={email}
                      skipRegistrationStep={
                        (doesUserExist?.hasMindsetId && doesUserExist?.hasProductAccount) ||
                        (doesUserExist?.hasMindsetId && Boolean(hcpReferralHash))
                      }
                    />
                  </div>
                </Slide>
              )}

              {step === 2 && (
                <Slide direction="left" in mountOnEnter unmountOnExit>
                  <div>
                    <RegisterScreen email={email} password={password} />
                  </div>
                </Slide>
              )}

              {step === 3 && (
                <Slide direction="left" in mountOnEnter unmountOnExit>
                  <div>
                    <ResetPasswordScreen setStep={setStep} email={email} />
                  </div>
                </Slide>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};
